<template>
  <div class="cage-verification-view">
    <div class="audit-container">
      <CageVerificationForm
        :cage-info="cageInfo"
        @verified="handleVerified"
        @cancel="handleCancel"
        @supervisor-override="handleSupervisorOverride"
      />

      <div v-if="isLoading || isProcessingCageLookup" class="loading-status">
        <div class="spinner"></div>
        <span>{{ isProcessingCageLookup ? 'Triggering cage audit...' : 'Processing...' }}</span>
      </div>

      <div v-if="error" class="error-banner"><strong>Error:</strong> {{ error }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuditStore } from '../../stores/audit.store';
import { useAuditSSE } from '../../composables/useAuditSSE';
import { requestCageLookup } from '../../services/audit.service';
import { getAccessToken } from '../../services/token-storage.service';
import CageVerificationForm from '../../components/audit/CageVerificationForm.vue';

const router = useRouter();
const auditStore = useAuditStore();
const correlationId = computed(() => auditStore.currentLookup?.correlationId || null);
const { connect } = useAuditSSE(correlationId);

const isLoading = computed(() => auditStore.isLoading);
const error = ref<string | null>(null);
const cageInfo = computed(() => auditStore.cageInfo);
const isProcessingCageLookup = ref(false);

/**
 * Handle cage number verification
 */
function handleVerified(cageNumber: string): void {
  console.info('Cage number verified', { cageNumber });
  // Navigate to remaining animals scanning
  router.push({ name: 'audit-scan-animals' });
}

/**
 * Handle cancel
 */
function handleCancel(): void {
  router.push({ name: 'audit-start' });
}

/**
 * Handle supervisor override - triggers cage lookup for both cages
 */
async function handleSupervisorOverride(cageNumber: string): Promise<void> {
  if (!cageInfo.value || !auditStore.currentLookup) {
    error.value = 'Missing cage information or lookup data';
    return;
  }

  const expectedCageNumber = cageInfo.value.number;
  const scannedBarcode = auditStore.currentLookup.rapidTagBarcode;

  if (cageNumber === expectedCageNumber) {
    // No mismatch, just proceed
    handleVerified(cageNumber);
    return;
  }

  // Cage mismatch detected - send lookup requests for both cages
  isProcessingCageLookup.value = true;
  error.value = null;

  try {
    const authToken = getAccessToken();
    if (!authToken) {
      throw new Error('No authentication token available');
    }

    // Prepare events for both cages
    if (!scannedBarcode) {
      throw new Error('Scanned barcode is required for cage lookup');
    }

    const expectedCageEvent = {
      cageNumber: expectedCageNumber,
      rapidTagBarcode: scannedBarcode,
    };
    const enteredCageEvent = {
      cageNumber: cageNumber,
      rapidTagBarcode: scannedBarcode,
    };

    console.log('🚀 [Cage Lookup] Sending cage lookup events:', {
      events: [
        {
          type: 'rapid-audit-animal-lookup-requested',
          cageNumber: expectedCageEvent.cageNumber,
          rapidTagBarcode: expectedCageEvent.rapidTagBarcode,
        },
        {
          type: 'rapid-audit-animal-lookup-requested',
          cageNumber: enteredCageEvent.cageNumber,
          rapidTagBarcode: enteredCageEvent.rapidTagBarcode,
        },
      ],
    });

    // Send cage lookup requests for both cages
    await Promise.all([
      requestCageLookup(expectedCageEvent, authToken),
      requestCageLookup(enteredCageEvent, authToken),
    ]);

    // Proceed with verification after sending events
    handleVerified(cageNumber);
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : 'Failed to trigger cage audit. Please try again.';
  } finally {
    isProcessingCageLookup.value = false;
  }
}

/**
 * Check if we have cage info, if not redirect to start
 */
onMounted(() => {
  if (!cageInfo.value) {
    // No cage info available, redirect to start
    router.push({ name: 'audit-start' });
    return;
  }

  // Check if barcode is in mock-data.json - if not, we shouldn't be here
  if (auditStore.currentLookup) {
    const scannedBarcode = auditStore.currentLookup.rapidTagBarcode;
    if (!scannedBarcode) {
      router.push({ name: 'audit-scan-animals' });
      return;
    }
    const KNOWN_MOCK_BARCODES = ['ABC123', 'DEF456', 'GHI789'];
    const isInMockData = KNOWN_MOCK_BARCODES.includes(scannedBarcode.toUpperCase());

    if (!isInMockData) {
      // Barcode not in mock-data.json, we shouldn't be on this page
      router.push({ name: 'audit-scan-animals' });
      return;
    }
  }

  // Connect SSE if not already connected
  if (!auditStore.sseConnected) {
    connect();
  }
});

onUnmounted(() => {
  // Don't disconnect SSE here - it might be needed for next step
  // disconnect();
});
</script>

<style scoped>
.cage-verification-view {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 0;
  padding: 0.5rem;
  background-color: #f9fafb;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.audit-container {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.25rem;
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (max-width: 768px) {
  .cage-verification-view {
    padding: 0.5rem;
  }

  .audit-container {
    padding: 1rem;
    gap: 0.875rem;
    border-radius: 0.375rem;
  }
}

.loading-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  color: #3b82f6;
}

.spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-banner {
  padding: 1rem;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  color: #991b1b;
}
</style>
